import { FormEvent, useId, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ApiError } from '@/lib/api';
import { useSigningBranding, useVerifyAccessCode } from '../hooks/useSigningSession';
import { BrandedSigningFrame } from './BrandedSigningFrame';

interface AccessCodeGateProps {
  token: string;
  /** True when the server has already reported the recipient locked out. */
  locked: boolean;
  /** Called after a successful verification; the caller reloads the session. */
  onVerified: () => void;
}

/**
 * The second-factor screen for a recipient whose sender set an access code.
 *
 * Shown when the session endpoint answers 401 `ACCESS_CODE_REQUIRED`. Nothing
 * about the envelope is loaded yet — by design, the session is exactly what
 * the code protects — so the page has only the token and the sender's
 * branding to work with. The branding read uses the pre-authentication
 * resolver server-side for that reason.
 *
 * The code is agreed out of band. This screen never hints at it, never says
 * how long it is, and reports a wrong code and a locked recipient
 * differently only because the recovery differs: retry, versus ask the
 * sender to set a new one.
 */
export function AccessCodeGate({ token, locked, onVerified }: AccessCodeGateProps) {
  const branding = useSigningBranding(token);
  const verify = useVerifyAccessCode(token);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLocked, setIsLocked] = useState(locked);
  const inputId = useId();
  const errorId = useId();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const trimmed = code.trim();
    if (trimmed.length === 0) {
      setError('Enter the access code the sender gave you.');
      return;
    }
    try {
      await verify.mutateAsync(trimmed);
      onVerified();
    } catch (err) {
      if (err instanceof ApiError && err.status === 423) {
        setIsLocked(true);
        return;
      }
      if (err instanceof ApiError && err.status === 400) {
        setError('That code is not correct. Check it with the sender and try again.');
        setCode('');
        return;
      }
      setError(err instanceof Error ? err.message : 'Could not verify the access code');
    }
  };

  return (
    <BrandedSigningFrame branding={branding.data?.branding}>
      <main className="max-w-md mx-auto px-4 py-12 space-y-6">
        {isLocked ? (
          <div className="space-y-3" role="alert">
            <h2 className="text-lg font-semibold">This signing link is locked</h2>
            <p className="text-sm text-muted-foreground">
              Too many incorrect access codes were entered. To continue, ask the sender to set a new
              code for you — they can do that without sending the document again.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <div className="space-y-2">
              <h2 className="text-lg font-semibold">Enter your access code</h2>
              <p className="text-sm text-muted-foreground">
                The sender protected this document with an access code and shared it with you
                separately — by phone or in person, not in the email that brought you here.
              </p>
            </div>
            <div className="space-y-1">
              <Label htmlFor={inputId}>Access code</Label>
              <Input
                id={inputId}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                autoComplete="one-time-code"
                autoFocus
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? errorId : undefined}
                disabled={verify.isPending}
              />
              {error && (
                <p id={errorId} className="text-sm text-destructive" role="alert">
                  {error}
                </p>
              )}
            </div>
            <Button type="submit" disabled={verify.isPending || code.trim().length === 0}>
              {verify.isPending ? 'Checking…' : 'Continue'}
            </Button>
          </form>
        )}
      </main>
    </BrandedSigningFrame>
  );
}
